'use client';

import { defaultLocale } from '@/i18n/locale-config';
import { cn } from '@/lib/utils';
import { de, enUS, fr } from 'date-fns/locale';
import { CalendarDays } from 'lucide-react';
import { useLocale } from 'next-intl';
import React, { forwardRef } from 'react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FieldError, UseFormRegister } from 'react-hook-form';
import ErrorText from './ErrorText';

interface DateInputProps {
    required?: boolean;
    className?: string;
    register?: UseFormRegister<any>;
    error?: FieldError;
    placeholder?: string;
    selectedDate: Date | null;
    // eslint-disable-next-line no-unused-vars
    onChange?: (date: Date | null) => void;
}

const DateInput = forwardRef<ReactDatePicker, DateInputProps>((props, ref) => {
    const {
        className,
        required,
        register,
        error,
        placeholder,
        selectedDate,
        onChange,
        ...inputProps
    } = props;

    const locale = useLocale();

    const today = new Date();

    const localeMap = {
        en: enUS,
        de: de,
        fr: fr,
    };

    const currentLocale = (locale in localeMap ? locale : defaultLocale) as keyof typeof localeMap;

    return (
        <div className="relative flex flex-col">
            <ReactDatePicker
                {...register}
                {...inputProps}
                ref={ref as React.Ref<ReactDatePicker>}
                selected={selectedDate}
                onChange={onChange}
                locale={localeMap[currentLocale]}
                className={cn(
                    'h-9 w-full border-b py-[10px] text-h6 font-medium text-black placeholder-dark-grey focus:border-brand focus:outline-none border-dark-grey placeholder-shown:border-grey bg-transparent rounded-none',
                    error && 'border-red-400',
                    className,
                )}
                placeholderText={required ? `${placeholder}*` : placeholder}
                required={required}
                dateFormat="dd/MM/yyyy"
                maxDate={today}
            />
            <CalendarDays
                width={24}
                height={24}
                className="absolute right-0 top-1 text-dark-grey pointer-events-none cursor-pointer"
            />
            {error && <ErrorText error={error} />}
        </div>
    );
});

DateInput.displayName = 'DateInput';

export default DateInput;
